import React, { Component } from 'react';
import { fetchApi } from '../../Network/NetworkService';
import '../../Assets/Pages/adminSuiviLists.css';

// Vue « funnel » : patients ayant rempli un suivi-dermato PUIS ouvert une
// nouvelle consultation (resultingRequestId renseigné côté feeli-io-server).
// Réutilise la CSS et la structure de SuiviDermatoList pour rester cohérent.

// Icône/couleur selon l'évolution cutanée déclarée au suivi (q1).
const evolutionConfig = {
    "Forte amélioration": { icon: 'zmdi-check-circle', cls: "green" },
    "Légère amélioration": { icon: 'zmdi-mood', cls: "orange" },
    "Aucun changement": { icon: 'zmdi-mood-bad', cls: "yellow" },
    "Cela s'aggrave": { icon: 'zmdi-mood-bad', cls: "red" },
};

// Couleur du statut de la consultation issue du suivi.
const stateConfig = {
    terminated: { cls: "green", label: "Terminée" },
    reviewed: { cls: "green", label: "Traitée" },
    approved: { cls: "orange", label: "Acceptée" },
    toCall: { cls: "orange", label: "À appeler" },
    waiting: { cls: "yellow", label: "En attente" },
    canceled: { cls: "red", label: "Annulée" },
    refunded: { cls: "red", label: "Remboursée" },
    incomplete: { cls: "gray", label: "Brouillon" },
};

const MONTHS_FR = [
    "janv.", "févr.", "mars", "avr.", "mai", "juin",
    "juil.", "août", "sept.", "oct.", "nov.", "déc.",
];

function pad(n) { return n < 10 ? `0${n}` : `${n}`; }

function patientName(s) {
    if (!s.userId) return "Patient inconnu";
    return `${s.userId.FName ?? ""} ${s.userId.LName ?? ""}`.trim() || "Patient inconnu";
}

function initials(f, l) {
    return `${(f?.[0] ?? "?").toUpperCase()}${(l?.[0] ?? "").toUpperCase()}`;
}

// Délai (jours) entre le suivi et la reconsultation qu'il a déclenchée.
function delayDays(s) {
    if (!s.resultingRequestId || !s.resultingRequestId.createdAt) return null;
    const ms = new Date(s.resultingRequestId.createdAt).getTime() - new Date(s.createdAt).getTime();
    return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
}

// YN Component
class YN extends Component {
    render() {
        const { value } = this.props;
        if (!value) return <span className="admin-sd-muted">—</span>;
        return <span className={`admin-sd-yn ${value === "Oui" ? "yes" : "no"}`}>{value}</span>;
    }
}

// Stat Component
class Stat extends Component {
    render() {
        const { label, value, color } = this.props;
        return (
            <div className="admin-sd-stat" style={{ "--accent": color }}>
                <p className="admin-sd-stat-label">{label}</p>
                <p className="admin-sd-stat-value">{value}</p>
            </div>
        );
    }
}

export class SuiviReconsultTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            data: [],
            loading: true,
            error: null,
            mounted: false,
            search: "",
            searchBy: "patientName",
            sort: "desc",
            filterEvolution: "all",
            page: 1,
        };

        this.pageSize = 8;
    }

    componentDidMount() {
        this.setState({ mounted: true });
        this._fetchSuivis();
    }

    _fetchSuivis() {
        this.setState({ loading: true, error: null });

        fetchApi('/suivi/dermato-reconsult', { method: 'GET' })
            .then(res => {
                this.setState({ data: res.data || [], loading: false });
            })
            .catch(err => {
                console.error('Error fetching suivi reconsultations:', err);
                this.setState({ error: err, loading: false });
            });
    }

    _getFiltered() {
        const { data, filterEvolution, search, searchBy, sort } = this.state;

        let rows = [...data];

        if (filterEvolution !== "all") {
            rows = rows.filter(r => r.q1_skinEvolution === filterEvolution);
        }
        if (search.trim()) {
            const q = search.toLowerCase();
            rows = rows.filter(r =>
                searchBy === "patientName"
                    ? patientName(r).toLowerCase().includes(q)
                    : (r.q1_skinEvolution || "").toLowerCase().includes(q)
            );
        }
        rows.sort((a, b) => {
            const da = new Date(a.createdAt).getTime();
            const db = new Date(b.createdAt).getTime();
            return sort === "desc" ? db - da : da - db;
        });
        return rows;
    }

    _getStats() {
        const { data } = this.state;
        const delays = data.map(delayDays).filter(d => d !== null);
        const avgDelay = delays.length
            ? Math.round(delays.reduce((a, b) => a + b, 0) / delays.length)
            : 0;
        return {
            total: data.length,
            wanted: data.filter(d => d.q4_reconsultDermatologist === "Oui").length,
            avgDelay,
            completed: data.filter(d =>
                d.resultingRequestId &&
                ["terminated", "reviewed"].includes(d.resultingRequestId.state)
            ).length,
        };
    }

    render() {
        const { mounted, search, searchBy, sort, filterEvolution, page, loading, error } = this.state;
        const filtered = this._getFiltered();
        const stats = this._getStats();

        const totalPages = Math.max(1, Math.ceil(filtered.length / this.pageSize));
        const currentPage = Math.min(page, totalPages);
        const paginated = filtered.slice(
            (currentPage - 1) * this.pageSize,
            currentPage * this.pageSize,
        );

        return (
            <div className="admin-sd-root">
                <div className="admin-sd-container">
                    {/* Header */}
                    <div className="admin-sd-header">
                        <div>
                            <h1>Suivis → reconsultation</h1>
                            <p>Patients ayant rempli un suivi dermatologique puis ouvert une nouvelle consultation.</p>
                        </div>
                        <span className="admin-sd-header-badge">{this.state.data.length} patients</span>
                    </div>

                    {/* Stats */}
                    <div className="admin-sd-stats">
                        <Stat label="Reconsultations" value={stats.total} color="#4e95fb" />
                        <Stat label="Voulaient reconsulter" value={stats.wanted} color="#02ce26" />
                        <Stat label="Délai moyen (jours)" value={stats.avgDelay} color="#2b7cd1" />
                        <Stat label="Consultations abouties" value={stats.completed} color="#8a5cf6" />
                    </div>

                    {/* Toolbar */}
                    <div className="admin-sd-toolbar">
                        <div className="admin-sd-search">
                            <i className="zmdi zmdi-search" />
                            <input
                                className="admin-sd-input"
                                placeholder={
                                    searchBy === "patientName"
                                        ? "Rechercher un patient..."
                                        : "Rechercher une évolution..."
                                }
                                value={search}
                                onChange={e => this.setState({ search: e.target.value, page: 1 })}
                            />
                        </div>
                        <select
                            className="admin-sd-select"
                            value={searchBy}
                            onChange={e => this.setState({ searchBy: e.target.value })}
                        >
                            <option value="patientName">Par patient</option>
                            <option value="evolution">Par évolution</option>
                        </select>
                        <select
                            className="admin-sd-select"
                            value={filterEvolution}
                            onChange={e => this.setState({ filterEvolution: e.target.value, page: 1 })}
                        >
                            <option value="all">Toutes les évolutions</option>
                            <option value="Forte amélioration">Forte amélioration</option>
                            <option value="Légère amélioration">Légère amélioration</option>
                            <option value="Aucun changement">Aucun changement</option>
                            <option value="Cela s'aggrave">Cela s'aggrave</option>
                        </select>
                        <button
                            className="admin-sd-btn"
                            onClick={() =>
                                this.setState(prev => ({ sort: prev.sort === "desc" ? "asc" : "desc" }))
                            }
                        >
                            <i className={sort === "desc" ? "zmdi zmdi-sort-amount-desc" : "zmdi zmdi-sort-amount-asc"} />
                            {sort === "desc" ? "Plus récents" : "Plus anciens"}
                        </button>
                    </div>

                    {/* Table */}
                    <div className="admin-sd-card">
                        {loading ? (
                            <div className="admin-sd-loader-wrap">
                                <div className="admin-sd-spinner"></div>
                                <p>Chargement des reconsultations...</p>
                            </div>
                        ) : error ? (
                            <div className="admin-sd-loader-wrap">
                                <i className="zmdi zmdi-alert-triangle" style={{ color: '#ff4858', fontSize: 36 }} />
                                <p>Erreur lors du chargement</p>
                            </div>
                        ) : (
                            <>
                                <div className="admin-sd-table-wrap">
                                    <table className="admin-sd-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 80 }}>Suivi</th>
                                                <th>Patient</th>
                                                <th>Évolution</th>
                                                <th className="admin-sd-th-center">Voulait reconsulter</th>
                                                <th style={{ width: 80 }}>Reconsult.</th>
                                                <th className="admin-sd-th-center">Délai</th>
                                                <th className="admin-sd-th-right">Statut consult.</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginated.length === 0 && (
                                                <tr>
                                                    <td colSpan={7} className="admin-sd-empty">
                                                        Aucune reconsultation ne correspond à votre recherche.
                                                    </td>
                                                </tr>
                                            )}
                                            {paginated.map(s => {
                                                const d = new Date(s.createdAt);
                                                const cfg = evolutionConfig[s.q1_skinEvolution] || {
                                                    icon: 'zmdi-help',
                                                    cls: "gray",
                                                };
                                                const rc = s.resultingRequestId;
                                                const rd = rc && rc.createdAt ? new Date(rc.createdAt) : null;
                                                const scfg = (rc && stateConfig[rc.state]) || { cls: "gray", label: rc ? rc.state : "—" };
                                                const delay = delayDays(s);
                                                return (
                                                    <tr key={s._id}>
                                                        <td>
                                                            <div className="admin-sd-date">
                                                                <span className="admin-sd-date-day">
                                                                    {mounted ? d.getDate() : d.getUTCDate()}
                                                                </span>
                                                                <span className="admin-sd-date-month">
                                                                    {MONTHS_FR[mounted ? d.getMonth() : d.getUTCMonth()]}
                                                                </span>
                                                                <span className="admin-sd-date-time">
                                                                    {mounted
                                                                        ? `${pad(d.getHours())}:${pad(d.getMinutes())}`
                                                                        : `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="admin-sd-patient">
                                                                <div className="admin-sd-avatar">
                                                                    {initials(s.userId?.FName, s.userId?.LName)}
                                                                </div>
                                                                <div className="admin-sd-patient-info">
                                                                    <div className="admin-sd-patient-name">{patientName(s)}</div>
                                                                    <div className="admin-sd-patient-email">
                                                                        {s.userId?.email ?? "—"}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className={`admin-sd-pill ${cfg.cls}`}>
                                                                <i className={`zmdi ${cfg.icon}`} />
                                                                {s.q1_skinEvolution}
                                                            </span>
                                                        </td>
                                                        <td className="admin-sd-td-center">
                                                            <YN value={s.q4_reconsultDermatologist} />
                                                        </td>
                                                        <td>
                                                            {rd ? (
                                                                <div className="admin-sd-date">
                                                                    <span className="admin-sd-date-day">
                                                                        {mounted ? rd.getDate() : rd.getUTCDate()}
                                                                    </span>
                                                                    <span className="admin-sd-date-month">
                                                                        {MONTHS_FR[mounted ? rd.getMonth() : rd.getUTCMonth()]}
                                                                    </span>
                                                                </div>
                                                            ) : (
                                                                <span className="admin-sd-muted">—</span>
                                                            )}
                                                        </td>
                                                        <td className="admin-sd-td-center">
                                                            {delay === null ? (
                                                                <span className="admin-sd-muted">—</span>
                                                            ) : (
                                                                <span className="admin-sd-pill gray">
                                                                    {delay === 0 ? "même jour" : `${delay} j`}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="admin-sd-td-right">
                                                            <span className={`admin-sd-pill ${scfg.cls}`}>
                                                                <i className="zmdi zmdi-assignment-account" />
                                                                {scfg.label}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                <div className="admin-sd-pagination">
                                    <span>
                                        Affichant{" "}
                                        <strong>
                                            {filtered.length === 0 ? 0 : (currentPage - 1) * this.pageSize + 1}
                                        </strong>
                                        {" – "}
                                        <strong>{Math.min(currentPage * this.pageSize, filtered.length)}</strong>
                                        {" sur "}
                                        <strong>{filtered.length}</strong>
                                    </span>
                                    <div className="admin-sd-pagination-controls">
                                        <button
                                            className="admin-sd-btn admin-sd-btn-icon"
                                            disabled={currentPage === 1}
                                            onClick={() =>
                                                this.setState(prev => ({ page: Math.max(1, prev.page - 1) }))
                                            }
                                        >
                                            <i className="zmdi zmdi-chevron-left" />
                                        </button>
                                        <span className="admin-sd-page-info">
                                            {currentPage} / {totalPages}
                                        </span>
                                        <button
                                            className="admin-sd-btn admin-sd-btn-icon"
                                            disabled={currentPage === totalPages}
                                            onClick={() =>
                                                this.setState(prev => ({
                                                    page: Math.min(totalPages, prev.page + 1),
                                                }))
                                            }
                                        >
                                            <i className="zmdi zmdi-chevron-right" />
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default SuiviReconsultTable;
